<script lang="ts">
  // UI
  import DeviceIcon from '$lib/components/DeviceIcon.svelte';
  // RoastLog
  import { selectedDeviceType, selectedRoaster } from '$lib/PersistedState.svelte';
  import { globalState } from '$lib/State.svelte';
  import { DeviceType } from '$lib/constants';
  import type { Device } from '$lib/devices';
  import { RLogBTLEDevice, RLogUSBDevice } from '$lib/devices/dataBridge';
  import { CoffeeTechDevice } from '$lib/devices/coffeeTech';
  import DiedrichDevice from '$lib/devices/diedrich';
  import FakeDevice from '$lib/devices/fake';
  import LoringDevice from '$lib/devices/loring';
  import PhidgetsDevice from '$lib/devices/phidgets';
  import PhidgetsRTDDevice from '$lib/devices/phidgets/rtd';
  import SivetzDevice from '$lib/devices/sivetz';
  import { SanFranciscanModbusDevice, SanFranciscanRTUDevice } from '$lib/devices/sanFranciscan';
  import PrimoDevice from '$lib/devices/primo';
  import { CoffedSR30Device, CoffedModbusDevice } from '$lib/devices/coffed';
  import YoshanRTUDevice from '$lib/devices/yoshan';
  import logger from '$lib/logger';

  // Other
  import { toast } from 'svelte-sonner';

  let device: Device;

  $effect(() => {
    // when there is a change of the device, disconnect first. Otherwise, the disconnected state
    // will trigger a disconnect of the *new* device type.
    //
    // Note that this effect may trigger even when the device is of the same type, strangely. To
    // guard against that look at the type and only run the updates when there is a different device.
    const isDifferentDeviceType = !device || (device && !selectedDeviceType.isType(device.type));

    if (device && isDifferentDeviceType && globalState.deviceConnected) {
      logger.info('Device effect change...disconnecting.', { deviceType: device.type });
      device.disconnect();
    }

    // This is a guard...this should never happen since the device selector prevents re-selecting
    // your current item/selection. Leaving this in for safety
    if (!isDifferentDeviceType) {
      logger.info('Device type effect but the device has not changed. NOOP.');
      return;
    }

    logger.info(`Setting device type effect: ${selectedDeviceType.value}`);

    switch (selectedDeviceType.value) {
      case DeviceType.PHIDGETS:
        device = new PhidgetsDevice();
        break;
      case DeviceType.PHIDGETS_RTD:
        device = new PhidgetsRTDDevice();
        break;
      case DeviceType.RLBTLE:
        device = new RLogBTLEDevice();
        break;
      case DeviceType.RLUSB:
        device = new RLogUSBDevice();
        break;
      case DeviceType.LORING:
        device = new LoringDevice();
        break;
      case DeviceType.DIEDRICH:
        device = new DiedrichDevice();
        break;
      case DeviceType.FAKE:
        device = new FakeDevice();
        break;
      case DeviceType.SIVETZ:
        device = new SivetzDevice();
        break;
      case DeviceType.COFFEE_TECH:
        device = new CoffeeTechDevice();
        break;
      case DeviceType.SAN_FRANCISCAN:
        device = new SanFranciscanModbusDevice();
        break;
      case DeviceType.SAN_FRANCISCAN_RTU:
        device = new SanFranciscanRTUDevice();
        break;
      case DeviceType.PRIMO:
        device = new PrimoDevice();
        break;
      case DeviceType.COFFED_SR30:
        device = new CoffedSR30Device();
        break;
      case DeviceType.COFFED_MODBUS:
        device = new CoffedModbusDevice();
        break;
      case DeviceType.YOSHAN_RTU:
        device = new YoshanRTUDevice();
        break;
      default:
        device = new FakeDevice();
    }
  });

  let buttonClasses = $derived(
    globalState.deviceConnected
      ? 'bg-green-500 hover:bg-red-400'
      : 'bg-red-400 hover:bg-red-50 hover:text-gray-500'
  );

  $effect(() => {
    if (!globalState.deviceConnected) {
      device.disconnect();
    }
  });

  const handleClick = async (e: MouseEvent) => {
    if (globalState.deviceConnecting) return;

    if (!globalState.deviceConnected) {
      const didConnect = await device.connect({
        // enumerate these to make TS happy, since the tcSettings is an [] vs. a 4-element array
        tcSettings: [
          selectedRoaster.tcSettings.current[0],
          selectedRoaster.tcSettings.current[1],
          selectedRoaster.tcSettings.current[2],
          selectedRoaster.tcSettings.current[3]
        ]
      });

      if (!didConnect) {
        toast.error(`Could not connect to ${selectedDeviceType.label}`);
      } else {
        toast.success(`Successfully connected to ${selectedDeviceType.label}`);
      }
    } else {
      device.disconnect();
    }
  };
</script>

{#if globalState.deviceConnecting}
  <button type="button" class="pointer-events-none -m-2.5 bg-yellow-400 p-2.5 dark:bg-yellow-500">
    <DeviceIcon deviceType={selectedDeviceType.value} isConnecting={true} />
  </button>
{:else}
  <button type="button" class="-m-2.5 p-2.5 {buttonClasses}" onclick={handleClick}>
    <DeviceIcon deviceType={selectedDeviceType.value} isConnecting={false} />
  </button>
{/if}
